import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  BookmarkIcon,
  CheckIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { filterAstToText, parseFilterExpr, type SavedFilter } from '@xphoto/shared';
import { usePopoverClamp } from '../components/usePopoverClamp';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { SavedFilterGlyph } from './saved-filter-icons';

/**
 * Menu « MES FILTRES » de la barre d'outils (déclencheur à côté de « Trier & filtrer »).
 * Liste les filtres ENREGISTRÉS (icône + nom ; clic = applique l'`expr`), surligne celui
 * dont l'expression correspond à l'`expr` courant (`aria-current` + COCHE — jamais la
 * couleur seule), et propose « Enregistrer le filtre actuel… », « Modifier l'expression… »,
 * « Gérer mes filtres… ». Mécanique d'accessibilité alignée sur `ToolsMenu`/`Menu` (rôle
 * `menu`, clavier fléché, `Échap`, clic extérieur). Ne possède AUCUN état métier : tout
 * (chargement, création, gestion) est orchestré par le parent via les callbacks.
 */

/** Forme canonique comparable d'une expression (parse → re-sérialise), ou `null` si invalide. */
function canonical(expr: string | undefined): string | null {
  if (expr === undefined || expr.trim() === '') return null;
  const parsed = parseFilterExpr(expr);
  return parsed.ok ? filterAstToText(parsed.ast) : null;
}

interface SavedFiltersMenuProps {
  filters: SavedFilter[];
  loading: boolean;
  errorMessage?: string | undefined;
  /** Expression avancée courante (`state.expr`) — pour surligner le filtre correspondant. */
  activeExpr: string | undefined;
  /** Applique l'`expr` d'un filtre enregistré (état propre côté parent). */
  onApply: (expr: string) => void;
  /** Ouvre la saisie « Enregistrer le filtre actuel… ». */
  onSaveCurrent: () => void;
  /** Ouvre l'éditeur d'expression avancée. */
  onEditExpr: () => void;
  /** Ouvre l'écran « Gérer mes filtres… ». */
  onManage: () => void;
}

export function SavedFiltersMenu({
  filters,
  loading,
  errorMessage,
  activeExpr,
  onApply,
  onSaveCurrent,
  onEditExpr,
  onManage,
}: SavedFiltersMenuProps): React.JSX.Element {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  // Maintien dans le cadre visible — panneau à largeur FIXE (`w-64`), donc le plus
  // débordant des trois menus de la barre (mesuré à −172 px sur 360 px de large).
  const panelRef = usePopoverClamp(open);

  const activeCanonical = useMemo(() => canonical(activeExpr), [activeExpr]);

  // Items FOCUSABLES du menu (filtres + actions), dans l'ordre de navigation clavier.
  type Item =
    | { kind: 'filter'; filter: SavedFilter; active: boolean }
    | { kind: 'action'; id: 'save' | 'edit' | 'manage'; separatorBefore: boolean };
  const items = useMemo<Item[]>(() => {
    const filterItems: Item[] = filters.map((f) => ({
      kind: 'filter',
      filter: f,
      active: activeCanonical !== null && canonical(f.expr) === activeCanonical,
    }));
    return [
      ...filterItems,
      { kind: 'action', id: 'save', separatorBefore: filterItems.length > 0 },
      { kind: 'action', id: 'edit', separatorBefore: false },
      { kind: 'action', id: 'manage', separatorBefore: false },
    ];
  }, [filters, activeCanonical]);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    setActiveIndex(0);
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open, close]);

  useEffect(() => {
    if (open) itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const onButtonKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    const count = items.length;
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + count) % count);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(count - 1);
        break;
      case 'Tab':
        close(false);
        break;
    }
  };

  const runAction = (id: 'save' | 'edit' | 'manage'): void => {
    close();
    if (id === 'save') onSaveCurrent();
    else if (id === 'edit') onEditExpr();
    else onManage();
  };

  const ACTION_LABEL: Record<'save' | 'edit' | 'manage', string> = {
    save: t('savedFilters.saveCurrent'),
    edit: t('savedFilters.editExpr'),
    manage: t('savedFilters.manage'),
  };
  const ACTION_ICON: Record<'save' | 'edit' | 'manage', React.ReactNode> = {
    save: <PlusIcon className="h-5 w-5" aria-hidden="true" />,
    edit: <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />,
    manage: <Cog6ToothIcon className="h-5 w-5" aria-hidden="true" />,
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('savedFilters.open')}
        title={t('savedFilters.open')}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onButtonKeyDown}
        className="flex h-11 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
      >
        <BookmarkIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="hidden sm:inline">{t('savedFilters.open')}</span>
        <ChevronDownIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={t('savedFilters.open')}
          onKeyDown={onMenuKeyDown}
          className="absolute end-0 z-20 mt-1 w-64 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* En-tête d'état : chargement / erreur / vide (rangées NON focusables). */}
          {loading && (
            <p className="flex items-center gap-2 px-3 py-2 text-sm text-muted" role="status">
              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('savedFilters.loading')}
            </p>
          )}
          {!loading && errorMessage && (
            <p role="alert" className="flex items-start gap-2 px-3 py-2 text-sm text-danger">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </p>
          )}
          {!loading && !errorMessage && filters.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted">{t('savedFilters.none')}</p>
          )}

          {items.map((item, index) => {
            if (item.kind === 'filter') {
              const { filter, active } = item;
              return (
                <button
                  key={`f:${filter.id}`}
                  ref={(el) => {
                    itemsRef.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  aria-current={active ? 'true' : undefined}
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => {
                    close();
                    onApply(filter.expr);
                  }}
                  title={interpolate(t('savedFilters.applyLabel'), { name: filter.name })}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-[current=true]:font-semibold motion-reduce:transition-none"
                >
                  <SavedFilterGlyph icon={filter.icon} className="h-5 w-5 shrink-0 text-muted" />
                  <span className="min-w-0 flex-1 truncate">{filter.name}</span>
                  {active && (
                    <CheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
                  )}
                </button>
              );
            }
            return (
              <div key={`a:${item.id}`}>
                {item.separatorBefore && (
                  <div role="separator" className="my-1 border-t border-border" />
                )}
                <button
                  ref={(el) => {
                    itemsRef.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={index === activeIndex ? 0 : -1}
                  onClick={() => runAction(item.id)}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
                >
                  {ACTION_ICON[item.id]}
                  <span className="flex-1">{ACTION_LABEL[item.id]}</span>
                </button>
              </div>
            );
          })}
          <span className="sr-only">{t('a11y.closeMenu')}</span>
        </div>
      )}
    </div>
  );
}
